/**
 * useGoogleAdsConversion · fires a Google Ads conversion event once
 * per page-load. Idempotent across React StrictMode double-renders.
 *
 * Director directive 2026.02 · "First Light" search strike.
 *
 * Configuration (frontend/.env — fill in conversion labels from
 * Google Ads → Tools & Settings → Conversions → click each conversion
 * → Tag setup → Use Google tag manager: copy the AW-xxxx/XXXXX label):
 *
 *   REACT_APP_GADS_AWID="AW-18151744142"
 *   REACT_APP_GADS_CONV_BOARDING="<label-string>"      // for $250 Privateer Boarding
 *   REACT_APP_GADS_CONV_HULL_SCAN="<label-string>"     // for $49 Hull-Scan Unlock
 *   REACT_APP_GADS_CONV_CATALOG="<label-string>"       // for variable-amount Catalog bundles
 *   REACT_APP_GADS_CONV_FUEL="<label-string>"          // for Fuel Tracker top-ups
 *
 * Until labels land, the hook fires a NAMED `event_label` so
 * Google Ads still sees the event (just won't optimize bidding
 * toward it). The moment a label is added the conversion goes live.
 */
import { useEffect, useRef } from "react";

const AWID = process.env.REACT_APP_GADS_AWID || "AW-18151744142";

const LABELS = {
    boarding:  process.env.REACT_APP_GADS_CONV_BOARDING  || null,
    hull_scan: process.env.REACT_APP_GADS_CONV_HULL_SCAN || null,
    catalog:   process.env.REACT_APP_GADS_CONV_CATALOG   || null,
    fuel:      process.env.REACT_APP_GADS_CONV_FUEL      || null,
};


/**
 * @param {Object} options
 * @param {"boarding"|"hull_scan"|"catalog"|"fuel"} options.event - which conversion to fire
 * @param {number}  [options.value]    - revenue value (USD)
 * @param {string}  [options.currency] - default "USD"
 * @param {string}  [options.transactionId] - dedupe key for Google Ads
 * @param {boolean} [options.armed=true] - gate the fire (e.g. wait for Stripe paid status)
 */
export default function useGoogleAdsConversion({ event, value, currency = "USD", transactionId, armed = true }) {
    const fired = useRef(false);

    useEffect(() => {
        if (!armed) return;
        if (fired.current) return;
        if (typeof window === "undefined" || typeof window.gtag !== "function") return;

        fired.current = true;
        const label = LABELS[event];
        const payload = {
            value: typeof value === "number" ? value : undefined,
            currency,
            transaction_id: transactionId || undefined,
        };

        if (label) {
            // Real conversion — Google Ads will count + optimize bids.
            window.gtag("event", "conversion", {
                send_to: `${AWID}/${label}`,
                ...payload,
            });
        } else {
            // No label yet — fire a fallback custom event so the
            // Director can confirm the wiring fires from each success
            // page. Add the label in env vars and Smart Bidding kicks in.
            window.gtag("event", `eigoo_${event}_success`, {
                send_to: AWID,
                event_category: "conversion_pending_label",
                event_label: event,
                ...payload,
            });
            // Single console.warn so the Director sees the missing wiring
            // when looking at the page source — silent in production.
            // eslint-disable-next-line no-console
            console.info(
                `[gtag] ${event} fired as fallback custom event — `
                + `set REACT_APP_GADS_CONV_${event.toUpperCase()} env var to upgrade to a tracked Google Ads conversion.`
            );
        }
    }, [armed, event, value, currency, transactionId]);
}
